// AdminChallengesPage — Create / edit / publish challenge tier rules
// All numbers (6% / 2% / 4% / 3% / 80% / 90%) editable inline

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface ChallengeTier {
  id: string;
  name: 'Beginner' | 'Mid' | 'Professional';
  accountSize: number;
  monthlyFee: number;
  cryptoPrice: number;
  resetPrice: number;
  profitTargetPct: number;
  dailyLossFundedPct: number;
  maxDDPct: number;
  contractsStd: number;
  contractsMicro: number;
  profitShareStart: number;
  profitShareAfter: number;
  consistencyEval: number;
  consistencyFunded: number;
  status: 'live' | 'draft' | 'archived';
  createdAt: string;
  subscribers: number;
}

const TIERS: ChallengeTier[] = [
  { id: 't1', name: 'Beginner',     accountSize: 50000,  monthlyFee: 79,  cryptoPrice: 69,  resetPrice: 69,  profitTargetPct: 6, dailyLossFundedPct: 2, maxDDPct: 4, contractsStd: 5,  contractsMicro: 50,  profitShareStart: 80, profitShareAfter: 90, consistencyEval: 40, consistencyFunded: 50, status: 'live',  createdAt: '2025-01-15', subscribers: 1840 },
  { id: 't2', name: 'Mid',          accountSize: 100000, monthlyFee: 149, cryptoPrice: 129, resetPrice: 129, profitTargetPct: 6, dailyLossFundedPct: 2, maxDDPct: 3, contractsStd: 10, contractsMicro: 100, profitShareStart: 80, profitShareAfter: 90, consistencyEval: 40, consistencyFunded: 50, status: 'live',  createdAt: '2025-01-15', subscribers: 3205 },
  { id: 't3', name: 'Professional', accountSize: 150000, monthlyFee: 219, cryptoPrice: 189, resetPrice: 189, profitTargetPct: 6, dailyLossFundedPct: 2, maxDDPct: 3, contractsStd: 15, contractsMicro: 150, profitShareStart: 80, profitShareAfter: 90, consistencyEval: 40, consistencyFunded: 50, status: 'live',  createdAt: '2025-01-15', subscribers: 1248 },
];

export function AdminChallengesPage() {
  const nav = useNavigate();
  const [tiers, setTiers] = useState(TIERS);
  const [editing, setEditing] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  function update(id: string, key: keyof ChallengeTier, value: any) {
    setTiers((curr) => curr.map((t) => (t.id === id ? { ...t, [key]: value } : t)));
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white pb-8">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-white/10 bg-[#0A0A0A]/80 backdrop-blur-xl px-2">
        <button onClick={() => nav('/admin/dashboard')} data-testid="back" className="flex h-9 w-9 items-center justify-center text-[#0A84FF] haptic">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold">Challenges</div>
        <button onClick={() => setShowCreate(true)} data-testid="add-tier" className="px-3 text-[15px] text-[#0A84FF]">+ New</button>
      </div>

      {/* Stats */}
      <div className="px-4 py-3 grid grid-cols-4 gap-2">
        <div className="rounded-[10px] bg-white/5 border border-white/10 p-2 text-center">
          <div className="text-[9px] text-white/60 uppercase">Tiers</div>
          <div className="text-[16px] font-bold">{tiers.length}</div>
        </div>
        <div className="rounded-[10px] bg-white/5 border border-white/10 p-2 text-center">
          <div className="text-[9px] text-white/60 uppercase">Live</div>
          <div className="text-[16px] font-bold text-[#34C759]">{tiers.filter((t) => t.status === 'live').length}</div>
        </div>
        <div className="rounded-[10px] bg-white/5 border border-white/10 p-2 text-center">
          <div className="text-[9px] text-white/60 uppercase">Subs</div>
          <div className="text-[16px] font-bold">{tiers.reduce((s, t) => s + t.subscribers, 0).toLocaleString()}</div>
        </div>
        <div className="rounded-[10px] bg-white/5 border border-white/10 p-2 text-center">
          <div className="text-[9px] text-white/60 uppercase">MRR</div>
          <div className="text-[16px] font-bold text-[#FF9500]">${(tiers.reduce((s, t) => s + t.subscribers * t.monthlyFee, 0) / 1000).toFixed(0)}k</div>
        </div>
      </div>

      {/* Tier list */}
      <div className="px-4 space-y-3">
        {tiers.map((t) => {
          const isEdit = editing === t.id;
          const tierColor = t.name === 'Beginner' ? '#34C759' : t.name === 'Mid' ? '#0A84FF' : '#1C1C1E';
          return (
            <div key={t.id} data-testid={`tier-${t.id}`} className="rounded-[16px] border border-white/10 bg-white/5 overflow-hidden">
              <button
                onClick={() => setEditing(isEdit ? null : t.id)}
                className="flex w-full items-center gap-3 px-4 py-3 text-left active:bg-white/5"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-[12px] text-[18px]" style={{ backgroundColor: tierColor + '20' }}>{t.name === 'Beginner' ? '🌱' : t.name === 'Mid' ? '🚀' : '💎'}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[15px] font-bold text-white">{t.name}</span>
                    <span className={['rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase',
                      t.status === 'live' ? 'bg-[#34C759]/20 text-[#34C759]' :
                      t.status === 'draft' ? 'bg-[#FF9500]/20 text-[#FF9500]' :
                      'bg-white/20 text-white/60'
                    ].join(' ')}>{t.status}</span>
                  </div>
                  <div className="text-[11px] text-white/60">${t.accountSize.toLocaleString()} account · ${t.monthlyFee}/mo · {t.subscribers.toLocaleString()} subscribers</div>
                </div>
                <svg viewBox="0 0 24 24" className={['h-4 w-4 text-white/40 transition-transform', isEdit ? 'rotate-90' : ''].join(' ')} fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </button>

              {isEdit && (
                <div className="border-t border-white/10 px-4 py-3 space-y-2.5">
                  <div className="grid grid-cols-2 gap-2">
                    <Field label="Account size"  value={t.accountSize}  onChange={(v) => update(t.id, 'accountSize', v)} test={`acc-${t.id}`} prefix="$" />
                    <Field label="Monthly fee"   value={t.monthlyFee}   onChange={(v) => update(t.id, 'monthlyFee', v)} test={`fee-${t.id}`} prefix="$" />
                    <Field label="Crypto price"  value={t.cryptoPrice}  onChange={(v) => update(t.id, 'cryptoPrice', v)} test={`crypto-${t.id}`} prefix="$" />
                    <Field label="Reset price"   value={t.resetPrice}   onChange={(v) => update(t.id, 'resetPrice', v)} test={`reset-${t.id}`} prefix="$" />
                    <Field label="Profit target" value={t.profitTargetPct} onChange={(v) => update(t.id, 'profitTargetPct', v)} test={`target-${t.id}`} suffix="%" />
                    <Field label="Daily loss (F)" value={t.dailyLossFundedPct} onChange={(v) => update(t.id, 'dailyLossFundedPct', v)} test={`dailyloss-${t.id}`} suffix="%" />
                    <Field label="Max DD"        value={t.maxDDPct}     onChange={(v) => update(t.id, 'maxDDPct', v)} test={`maxdd-${t.id}`} suffix="%" />
                    <Field label="Profit share (start)" value={t.profitShareStart} onChange={(v) => update(t.id, 'profitShareStart', v)} test={`split-${t.id}`} suffix="%" />
                    <Field label="Profit share (after 3rd)" value={t.profitShareAfter} onChange={(v) => update(t.id, 'profitShareAfter', v)} test={`split2-${t.id}`} suffix="%" />
                    <Field label="Consistency eval" value={t.consistencyEval} onChange={(v) => update(t.id, 'consistencyEval', v)} test={`consisteval-${t.id}`} suffix="%" />
                    <Field label="Consistency funded" value={t.consistencyFunded} onChange={(v) => update(t.id, 'consistencyFunded', v)} test={`consistfund-${t.id}`} suffix="%" />
                    <Field label="Std contracts" value={t.contractsStd} onChange={(v) => update(t.id, 'contractsStd', v)} test={`std-${t.id}`} />
                  </div>
                  <div className="flex gap-2 mt-2">
                    <button onClick={() => setEditing(null)} data-testid={`save-${t.id}`} className="flex-1 h-9 rounded-full bg-[#0A84FF] text-white text-[12px] font-bold active:opacity-40">Save changes</button>
                    <button onClick={() => update(t.id, 'status', t.status === 'live' ? 'archived' : 'live')} data-testid={`toggle-${t.id}`} className="flex-1 h-9 rounded-full bg-white/10 text-white text-[12px] font-semibold active:opacity-40">
                      {t.status === 'live' ? 'Archive' : 'Publish'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 backdrop-blur-sm" onClick={() => setShowCreate(false)}>
          <div className="w-full max-w-[420px] rounded-t-[20px] bg-[#1C1C1E] p-4 text-white" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-[18px] font-bold mb-3">Create new tier</h2>
            <p className="text-[12px] text-white/60 mb-3">Define account size, fees, and risk limits. Tiers start as drafts until published.</p>
            <div className="rounded-[12px] bg-white/5 border border-white/10 p-3 text-[12px] text-white/70 mb-3">All new tiers inherit Malta jurisdiction, 8 platforms, and 2-min news rule by default.</div>
            <div className="flex gap-2">
              <button onClick={() => setShowCreate(false)} className="flex-1 h-10 rounded-full bg-white/10 text-[13px] font-semibold active:opacity-40">Cancel</button>
              <button onClick={() => { alert('Tier created as draft'); setShowCreate(false); }} data-testid="create-tier-confirm" className="flex-1 h-10 rounded-full bg-[#0A84FF] text-[13px] font-bold active:opacity-40">Create draft</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, test, prefix, suffix }: { label: string; value: number; onChange: (v: number) => void; test: string; prefix?: string; suffix?: string }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-wider text-white/50 mb-1">{label}</label>
      <div className="relative">
        {prefix && <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[12px] text-white/40">{prefix}</span>}
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          data-testid={test}
          className={['w-full h-8 rounded-[8px] bg-white/5 border border-white/10 text-[13px] text-white tabular-nums focus:outline-none focus:ring-1 focus:ring-[#0A84FF]',
            prefix ? 'pl-6' : 'pl-2.5',
            suffix ? 'pr-6' : 'pr-2.5'
          ].join(' ')}
        />
        {suffix && <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[12px] text-white/40">{suffix}</span>}
      </div>
    </div>
  );
}
